import { CHAPTERS } from '../data/chapters.js';
import { parseTime } from './clock.js';
import { weekdayOf } from './npc.js';

/**
 * The gate.
 *
 * A chapter arms when a department crosses its threshold and stays armed until
 * it is played. While one is armed, the day cannot end — which is the entire
 * mechanism, and the reason it lives in the simulation rather than in the UI:
 * whatever ends a day has to ask this module for permission, and there is
 * exactly one way to get it.
 *
 * Crossing is edge-triggered against the last reading, not level-triggered
 * against the threshold. Level-triggering would re-arm the same beat every
 * frame you spent under the line, and once played a chapter never fires again
 * — you do not get the same conversation twice for drifting back over it.
 */
/** Beats that changed id, old to new. The joint was Errol's before it was Alex's. */
const RENAMED = Object.freeze({ 'ch-errol-joint': 'ch-alex-joint' });

/*
 * `has` answers for the story: whether a flag it keeps is set. Three optional
 * conditions on a beat read it, and a beat that uses none of them is exactly
 * what it was before there was a story:
 *
 *   needs    a flag that must be set: something the story says has happened
 *   unless   a flag that must not be: somebody the beat is about has gone
 *   follows  another beat that must have been played first, for two beats
 *            that are one scene in two places
 */
export function createChapters({ standing, catalogue = CHAPTERS, has = () => false }) {
  let played = new Set();
  let pending = null;

  function crosses(rule, value) {
    if (rule.below !== undefined) return value <= rule.below;
    if (rule.above !== undefined) return value >= rule.above;
    return false;
  }

  return {
    get pending() {
      return pending;
    },
    /** What stops the day ending. Read by whatever advances the calendar. */
    get blocking() {
      return Boolean(pending);
    },
    get played() {
      return [...played];
    },
    /**
     * The beats themselves, so nobody has to restate one of their numbers.
     *
     * Read-only on purpose. A headless script that wants to stand the player
     * at Alex's line asks the beat where its line is; the alternative is a
     * copy of the threshold in a second file, which is how `opening.mjs` came
     * to be holding a stale 30 after the line moved.
     */
    get catalogue() {
      return catalogue;
    },

    /**
     * Arms the first unplayed chapter whose line has been crossed.
     *
     * One at a time: two beats queued behind each other is two modals, and the
     * second one would be read as an error.
     */
    update({ room = null, shiftOver = false, day = null, minutes = null, allow = () => true } = {}) {
      if (pending) return pending;
      for (const chapter of catalogue) {
        // Kept back by the guided first days (`tutorial.js`) until its turn.
        if (!allow(chapter)) continue;
        // A beat that is not written yet is in the list so it is not
        // forgotten, and is never armed.
        if (chapter.stub || !chapter.when) continue;
        if (played.has(chapter.id)) continue;
        /*
         * A beat with a place happens there: it arms only when you are
         * standing in it — and, if it says `after: 'shift'`, with the shift
         * behind you — so it never holds the one pending slot while it waits
         * for you to turn up.
         */
        if (chapter.where && room !== chapter.where) continue;
        /*
         * And when, for a beat that is about something only true some days or
         * some hours: the same `days` a roster row keeps, and a window like a
         * shift's. A beat that asks for a man who is off at four is not asked
         * at five.
         */
        if (chapter.days && (day === null || !chapter.days.includes(weekdayOf(day)))) continue;
        if (chapter.hours && (minutes === null || minutes < parseTime(chapter.hours[0]) || minutes >= parseTime(chapter.hours[1]))) continue;
        if (chapter.after === 'shift' && !shiftOver) continue;
        if (chapter.needs && !has(chapter.needs)) continue;
        if (chapter.unless && has(chapter.unless)) continue;
        if (chapter.follows && !played.has(chapter.follows)) continue;
        if (!crosses(chapter.when, standing.get(chapter.when.department))) continue;
        pending = chapter;
        return pending;
      }
      return null;
    },

    /**
     * The beats with a place that would play the moment you walked into it:
     * every other condition met. For the marker over the person who gives it
     * and the mark on the map where it plays, so a mission that starts from a
     * person is never a thing you find only by wandering into the right room.
     */
    waiting({ shiftOver = false, day = null, minutes = null, allow = () => true } = {}) {
      if (pending) return [];
      return catalogue.filter((chapter) => {
        if (chapter.stub || !chapter.when || !chapter.where) return false;
        if (played.has(chapter.id) || !allow(chapter)) return false;
        if (chapter.days && (day === null || !chapter.days.includes(weekdayOf(day)))) return false;
        if (chapter.hours && (minutes === null || minutes < parseTime(chapter.hours[0]) || minutes >= parseTime(chapter.hours[1]))) return false;
        if (chapter.after === 'shift' && !shiftOver) return false;
        if (chapter.needs && !has(chapter.needs)) return false;
        if (chapter.unless && has(chapter.unless)) return false;
        if (chapter.follows && !played.has(chapter.follows)) return false;
        return crosses(chapter.when, standing.get(chapter.when.department));
      });
    },

    /** Marks it read. The only way to clear the gate. */
    play() {
      if (!pending) return null;
      const chapter = pending;
      played.add(chapter.id);
      pending = null;
      return chapter;
    },

    clear() {
      played = new Set();
      pending = null;
    },

    toJSON: () => ({ played: [...played], pending: pending?.id ?? null }),
    load(state) {
      // A beat that has been renamed is the same beat: a save that played it
      // under its old name has played it.
      played = new Set((state?.played ?? []).map((id) => RENAMED[id] ?? id));
      pending = state?.pending ? catalogue.find((c) => c.id === state.pending) ?? null : null;
    },
  };
}
